/**
 * Comprehensive input validation library for security
 */

/**
 * Validates and sanitizes a text input
 * Removes potentially dangerous content
 */
export function sanitizeText(input: string): string {
  if (!input) return '';
  
  // Convert to string if not already
  const text = String(input).trim();
  
  // Basic sanitization of potential malicious content
  return text
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/<\/?[^>]+(>|$)/g, ''); // Simple HTML tag removal
}

/**
 * Validates an email address for format and basic security
 */
export function validateEmail(email: string): { valid: boolean; message?: string; suggestion?: string } {
  if (!email) {
    return { valid: false, message: 'Email is required' };
  }
  
  // Remove any whitespace
  const cleanEmail = email.trim();
  
  // Simpler email regex that covers most cases
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailRegex.test(cleanEmail)) {
    // Check for some common errors
    if (!cleanEmail.includes('@')) {
      return { valid: false, message: 'Email must contain @ symbol' };
    }
    if (cleanEmail.split('@').length > 2) {
      return { valid: false, message: 'Email cannot contain multiple @ symbols' };
    }
    if (cleanEmail.endsWith('@')) {
      return { valid: false, message: 'Email must include a domain after @ symbol' };
    }
    
    return { valid: false, message: 'Invalid email format' };
  }
  
  // Length check
  if (cleanEmail.length > 254) {
    return { valid: false, message: 'Email is too long' };
  }
  
  // Check for common domain typos but don't invalidate the email
  // Simplified to just the most common ones
  const domain = cleanEmail.split('@')[1].toLowerCase();
  const commonTypos: Record<string, string> = {
    'gmial.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'hotmial.com': 'hotmail.com',
    'yaho.com': 'yahoo.com',
    'outloo.com': 'outlook.com',
  };
  
  // If a typo is detected, provide a suggestion but still consider the email valid
  if (commonTypos[domain]) {
    const suggestedEmail = `${cleanEmail.split('@')[0]}@${commonTypos[domain]}`;
    return { 
      valid: true, 
      suggestion: suggestedEmail,
      message: `Did you mean ${suggestedEmail}?` 
    };
  }
  
  return { valid: true };
}

/**
 * Validates a URL for format and security
 */
export function validateURL(url: string): { valid: boolean; message?: string; sanitized?: string } {
  if (!url) {
    return { valid: false, message: 'URL is required' };
  }
  
  let cleanUrl = url.trim();
  
  // Check for dangerous protocols
  if (/^(?:javascript|data|vbscript|file):/i.test(cleanUrl)) {
    return { valid: false, message: 'URL contains an invalid protocol' };
  }
  
  try {
    // Add https:// prefix if protocol is missing
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    // Validate URL format
    new URL(cleanUrl);
    
    return { valid: true, sanitized: cleanUrl };
  } catch (error) {
    return { valid: false, message: 'Invalid URL format' };
  }
}

/**
 * Validate a password against security requirements
 */
export function validatePassword(password: string): { 
  valid: boolean; 
  message?: string;
  strength: 'weak' | 'medium' | 'strong' | 'very-strong'; 
} {
  if (!password) {
    return { valid: false, message: 'Password is required', strength: 'weak' };
  }
  
  // Minimum length
  if (password.length < 8) {
    return { 
      valid: false, 
      message: 'Password must be at least 8 characters', 
      strength: 'weak' 
    };
  }
  
  // Basic strength calculation
  let strength = 0;
  
  // Has uppercase letter
  if (/[A-Z]/.test(password)) strength++;
  
  // Has lowercase letter
  if (/[a-z]/.test(password)) strength++;
  
  // Has number
  if (/[0-9]/.test(password)) strength++;
  
  // Has special character
  if (/[^A-Za-z0-9]/.test(password)) strength++;
  
  // Length bonus
  if (password.length >= 12) strength++;
  
  // Check for obvious weak passwords
  if (/^123|password|admin|qwerty|welcome/i.test(password)) {
    strength = Math.max(0, strength - 2);
  }
  
  // Determine if password is valid - must have at least 3 character types
  const requirementsMet = strength >= 3;
  const valid = requirementsMet;
  
  let message: string | undefined;
  if (!valid) {
    message = 'Password must include at least 3 of: uppercase, lowercase, number, and special character';
  }
  
  // Determine strength label
  let strengthLabel: 'weak' | 'medium' | 'strong' | 'very-strong';
  if (strength <= 2) {
    strengthLabel = 'weak';
  } else if (strength <= 3) {
    strengthLabel = 'medium';
  } else if (strength <= 4) {
    strengthLabel = 'strong';
  } else {
    strengthLabel = 'very-strong';
  }
  
  return { 
    valid, 
    message, 
    strength: strengthLabel
  };
}

/**
 * Validates a number input
 */
export function validateNumber(
  input: string | number, 
  options: { min?: number; max?: number } = {}
): { valid: boolean; message?: string; value?: number } {
  if (input === '' || input === null || input === undefined) {
    return { valid: false, message: 'Number is required' };
  }
  
  // Convert to string if not already
  const inputStr = String(input).trim();
  
  // Check if it's a valid number
  const num = Number(inputStr);
  
  if (isNaN(num)) {
    return { valid: false, message: 'Input must be a valid number' };
  }
  
  // Check minimum value if provided
  if (options.min !== undefined && num < options.min) {
    return { valid: false, message: `Number must be at least ${options.min}` };
  }
  
  // Check maximum value if provided
  if (options.max !== undefined && num > options.max) {
    return { valid: false, message: `Number must be at most ${options.max}` };
  }
  
  return { valid: true, value: num };
}

/**
 * Validates that a value is in a list of allowed values
 */
export function validateEnum<T extends string>(
  value: string,
  allowedValues: T[]
): { valid: boolean; message?: string; value?: T } {
  if (!value) {
    return { valid: false, message: 'Value is required' };
  }
  
  if (!allowedValues.includes(value as T)) {
    return { 
      valid: false, 
      message: `Value must be one of: ${allowedValues.join(', ')}` 
    };
  }
  
  return { valid: true, value: value as T };
}

/**
 * Validates a file before upload
 */
export function validateFile(
  file: File,
  options: {
    maxSizeBytes?: number;
    allowedTypes?: string[];
    allowedExtensions?: string[];
  } = {}
): { valid: boolean; message?: string } {
  if (!file) {
    return { valid: false, message: 'File is required' };
  }
  
  // Default max size: 5MB
  const maxSize = options.maxSizeBytes || 5 * 1024 * 1024;
  
  // Check file size
  if (file.size > maxSize) {
    const sizeMB = Math.round(maxSize / (1024 * 1024));
    return { valid: false, message: `File size exceeds maximum allowed (${sizeMB}MB)` };
  }
  
  // Check file type if allowed types are specified
  if (options.allowedTypes && options.allowedTypes.length > 0) {
    if (!options.allowedTypes.includes(file.type)) {
      return { 
        valid: false, 
        message: `File type not allowed. Allowed types: ${options.allowedTypes.join(', ')}` 
      };
    }
  }
  
  // Check file extension if allowed extensions are specified
  if (options.allowedExtensions && options.allowedExtensions.length > 0) {
    const fileName = file.name || '';
    const fileExt = fileName.split('.').pop()?.toLowerCase() || '';
    
    if (!options.allowedExtensions.includes(`.${fileExt}`)) {
      return { 
        valid: false, 
        message: `File extension not allowed. Allowed extensions: ${options.allowedExtensions.join(', ')}` 
      };
    }
  }
  
  return { valid: true };
}

/**
 * Validates a phone number format
 */
export function validatePhoneNumber(phone: string): { valid: boolean; message?: string; formatted?: string } {
  if (!phone) {
    return { valid: false, message: 'Phone number is required' };
  }
  
  // Remove all non-numeric characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Basic length check
  if (digitsOnly.length < 10 || digitsOnly.length > 15) {
    return { valid: false, message: 'Phone number must be 10-15 digits' };
  }
  
  // Check for obviously fake numbers
  if (/^(.)\1+$/.test(digitsOnly)) {
    return { valid: false, message: 'Invalid phone number (repeated digits)' };
  }
  
  // Simple formatting - just return cleaned digits with a + prefix
  const formatted = `+${digitsOnly}`;
  
  return { valid: true, formatted };
}

/**
 * Validates a date input
 */
export function validateDate(
  date: string | Date,
  options: {
    minDate?: Date;
    maxDate?: Date;
    allowFutureDates?: boolean;
  } = {}
): { valid: boolean; message?: string; date?: Date } {
  if (!date) {
    return { valid: false, message: 'Date is required' };
  }
  
  let parsedDate: Date;
  
  if (date instanceof Date) {
    parsedDate = date;
  } else {
    // Parse the string to a Date object
    parsedDate = new Date(date);
  }
  
  // Check if the date is valid
  if (isNaN(parsedDate.getTime())) {
    return { valid: false, message: 'Invalid date format' };
  }
  
  // Check minimum date if provided
  if (options.minDate && parsedDate < options.minDate) {
    return { valid: false, message: 'Date is too early' };
  }
  
  // Check maximum date if provided
  if (options.maxDate && parsedDate > options.maxDate) {
    return { valid: false, message: 'Date is too late' };
  }
  
  // Simplified future date check
  if (options.allowFutureDates === false && parsedDate > new Date()) {
    return { valid: false, message: 'Future dates are not allowed' };
  }
  
  return { valid: true, date: parsedDate };
} 